import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataPanel,
  DataPanelInlineItem,
  DataPanelRow,
  NgButton,
  PanelErrorWithIcon,
  PanelInfoWithIcon,
  PositionSourceBadge,
} from '@/ui';
import { useIncidentHubEvent } from '@ngsos/portal-core/portal';

import { PositionSource, WebAppState } from '@/signalr';
import type { Caller, DeviceInfo, Position } from '@/signalr';
import {
  locateCaller as locateCallerCommand,
  openManualGpsCoordinatesDialog,
} from '../../actions';
import { useIncidentRealtime } from '../../hub/incidentRealtime';
import { formatCoordinates, formatCoordinatesToDegree } from '../../model/formatters';
import { toUiPositionSource } from '../../model/positionSource';
import { IncidentDetailLocationSmsSentInfo } from '../../IncidentDetailLocationSmsSentInfo';
import { MapLinksPanelItem } from '../MapLinksPanelItem';
import { ApproximateAddressPanelItem } from './ApproximateAddressPanelItem';
import { DeviceInfoPanelRow } from './DeviceInfoPanelRow';
import { PositionInfoPanelRow } from './PositionInfoPanelRow';

export type CallerLocationPanelProps = {
  incidentId: string;
  incidentIsReadOnly: boolean;
  callerPosition: Position | null;
  callerPositionHistory: readonly Position[];
  deviceInfo: DeviceInfo;
  caller: Caller;
  chatIsActive: boolean;
  eedBatteryLevel?: string | null;
};

function isLocationRelatedState(webAppState: WebAppState): boolean {
  return (
    webAppState === WebAppState.None ||
    webAppState === WebAppState.LocationSmsSent ||
    webAppState === WebAppState.AppOpened ||
    webAppState === WebAppState.LocationPermissionRequested ||
    webAppState === WebAppState.LocationPermissionGranted ||
    webAppState === WebAppState.LocationUnavailableNotSupported ||
    webAppState === WebAppState.LocationPermissionDenied ||
    webAppState === WebAppState.LocationPositionUnavailable ||
    webAppState === WebAppState.LocationTimeout
  );
}

function isLocationErrorState(webAppState: WebAppState): boolean {
  return (
    webAppState === WebAppState.LocationUnavailableNotSupported ||
    webAppState === WebAppState.LocationPermissionDenied ||
    webAppState === WebAppState.LocationPositionUnavailable ||
    webAppState === WebAppState.LocationTimeout
  );
}

export function CallerLocationPanel({
  incidentId,
  incidentIsReadOnly,
  callerPosition,
  callerPositionHistory,
  deviceInfo,
  caller,
  chatIsActive,
  eedBatteryLevel,
}: CallerLocationPanelProps) {
  const { t } = useTranslation();
  const realtime = useIncidentRealtime();
  const [webAppState, setWebAppState] = useState<WebAppState>(WebAppState.None);

  useIncidentHubEvent(incidentId, 'WebAppStateChanged', (newState) => {
    setWebAppState(newState);
  });

  useEffect(() => {
    if (!realtime) {
      return;
    }
    let cancelled = false;
    void realtime
      .getLiveState()
      .then((liveState) => {
        if (!cancelled) {
          setWebAppState(liveState.webAppState);
        }
      })
      .catch(() => {
      });
    return () => {
      cancelled = true;
    };
  }, [realtime]);

  const locationStateText = (): string => {
    switch (webAppState) {
      case WebAppState.None:
      case WebAppState.LocationSmsSent:
        return t('WebAppState_Location_WaitingForLinkOpen');
      case WebAppState.AppOpened:
        return t('WebAppState_Location_AppOpened');
      case WebAppState.LocationPermissionRequested:
        return t('WebAppState_Location_PermissionRequested');
      case WebAppState.LocationPermissionGranted:
        return t('WebAppState_Location_PermissionGranted');
      case WebAppState.LocationUnavailableNotSupported:
        return t('WebAppState_Location_Unsupported');
      case WebAppState.LocationPermissionDenied:
        return t('WebAppState_Location_PermissionDenied');
      case WebAppState.LocationPositionUnavailable:
        return t('WebAppState_Location_PositionUnavailable');
      case WebAppState.LocationTimeout:
        return t('WebAppState_Location_Timeout');
      default:
        return t('WebAppState_Location_WaitingForLinkOpen');
    }
  };

  const locationStateDescription = (): string => {
    switch (webAppState) {
      case WebAppState.None:
      case WebAppState.LocationSmsSent:
        return t('WebAppState_Location_WaitingForLinkOpen_Description');
      case WebAppState.LocationPermissionRequested:
        return t('WebAppState_Location_PermissionRequested_Description');
      case WebAppState.LocationPermissionGranted:
        return t('WebAppState_Location_PermissionGranted_Description');
      case WebAppState.LocationUnavailableNotSupported:
        return t('WebAppState_Location_Unsupported_Description');
      case WebAppState.LocationPermissionDenied:
        return t('WebAppState_Location_PermissionDenied_Description');
      case WebAppState.LocationPositionUnavailable:
        return t('WebAppState_Location_PositionUnavailable_Description');
      case WebAppState.LocationTimeout:
        return t('WebAppState_Location_Timeout_Description');
      default:
        return '';
    }
  };

  const locateCallerConfirmationMessage = chatIsActive ? t('Incident_InfoPanel_ConfirmLocateCaller') : undefined;

  const locateCaller = async () => {
    await locateCallerCommand(incidentId);
  };

  const openManualGpsEditDialog = () => {
    openManualGpsCoordinatesDialog(incidentId);
  };

  if (callerPosition === null) {
    return (
      <div>
        <h2>{t('Incident_InfoPanel_CallerLocationHeader')}</h2>

        <div className="bg-light p-2">
          {isLocationRelatedState(webAppState) && webAppState !== WebAppState.None ? (
            isLocationErrorState(webAppState) ? (
              <div className="mb-2">
                <PanelErrorWithIcon title={locationStateText()} description={locationStateDescription()} />
              </div>
            ) : (
              <>
                <PanelInfoWithIcon icon="fal fa-location-crosshairs" text={locationStateText()} />
                {locationStateDescription() !== '' && (
                  <div className="text-center small text-black-50 mt-1">{locationStateDescription()}</div>
                )}
              </>
            )
          ) : (
            <PanelInfoWithIcon icon="fal fa-location-crosshairs-slash" text={t('Incident_InfoPanel_UnknownCallerLocation')} />
          )}

          {!incidentIsReadOnly && (
            <>
              <div className="text-center small text-black-50 mt-2 mb-1">
                {t('Incident_InfoPanel_UnknownCallerLocation_Instructions')}
              </div>

              <div className="d-flex flex-wrap justify-content-center gap-1 mt-2">
                <NgButton
                  text={t('Incident_OpenEditGpsLocationDialog_NoPosition')}
                  icon="fal fa-pen-to-square"
                  size="small"
                  onClick={openManualGpsEditDialog}
                />

                <NgButton
                  text={t('NewIncidentDialog_Button_Localize')}
                  icon="fal fa-location"
                  size="small"
                  disabled={caller.phoneNumbers.length === 0}
                  enableMultipleClickPrevention
                  showProgress
                  minimumProgressTimeOutMs={3000}
                  confirmationMessage={locateCallerConfirmationMessage}
                  onClick={locateCaller}
                />
              </div>

              {caller.phoneNumbers.length > 0 && <IncidentDetailLocationSmsSentInfo />}
            </>
          )}
        </div>
      </div>
    );
  }

  const allowApproximateAddress = callerPosition.positionSource !== PositionSource.Eed;

  return (
    <div>
      <h2 className="h5 mb-2">{t('Incident_InfoPanel_CallerLocationHeader')}</h2>

      <div className="d-flex justify-content-between align-items-center mb-1">
        <PositionSourceBadge positionSource={toUiPositionSource(callerPosition.positionSource)} />
        <MapLinksPanelItem coordinates={callerPosition} />
      </div>

      <DataPanel>
        <DataPanelRow>
          <DataPanelInlineItem
            title={t('Incident_InfoPanel_Coordinates')}
            value={formatCoordinatesToDegree(callerPosition)}
            allowCopyToClipboard
            valueToCopy={formatCoordinates(callerPosition)}
          ></DataPanelInlineItem>
        </DataPanelRow>

        {allowApproximateAddress && <ApproximateAddressPanelItem coordinates={callerPosition} />}

        {callerPosition.positionSource !== PositionSource.Manual && (
          <PositionInfoPanelRow lastIncidentPosition={callerPosition} />
        )}

        {!incidentIsReadOnly && callerPosition.positionSource === PositionSource.Manual && (
          <DataPanelRow>
            <div className="d-flex flex-row gap-1 mt-2 w-100">
              <NgButton
                text={t('Incident_OpenEditGpsLocationDialog_ChangePosition')}
                icon="fal fa-pen-to-square"
                size="small"
                cssClass="w-100"
                onClick={openManualGpsEditDialog}
              />

              {callerPositionHistory.every(
                (position) =>
                  position.positionSource === PositionSource.Manual || position.positionSource === PositionSource.Aml,
              ) && (
                <NgButton
                  text={t('NewIncidentDialog_Button_Localize')}
                  icon="fal fa-location"
                  size="small"
                  disabled={caller.phoneNumbers.length === 0}
                  enableMultipleClickPrevention
                  showProgress
                  minimumProgressTimeOutMs={3000}
                  cssClass="w-100"
                  confirmationMessage={locateCallerConfirmationMessage}
                  onClick={locateCaller}
                />
              )}
            </div>
          </DataPanelRow>
        )}

        <DeviceInfoPanelRow deviceInfo={deviceInfo} eedBatteryLevel={eedBatteryLevel} />
      </DataPanel>
    </div>
  );
}
